import {
  CalendarOutlined,
  LeftCircleOutlined,
  RightCircleOutlined,
} from '@ant-design/icons';
import { SupportChatWidget } from '@features/SupportChat';
import { Button, Dropdown, Layout, Menu } from 'antd';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { LogoutModal } from '@app/components';
import { AppLoader } from '@app/components/AppLoader';
import { Avatar } from '@app/components/Avatar';
import { BillingBlockedStub } from '@app/components/BillingBlockedStub';
import { BranchSelect } from '@app/components/BranchSelect';
import { Dialog } from '@app/components/Dialog';
import { HelpBoxModal } from '@app/components/Modals';
import { OrganizationSelect } from '@app/components/OrganizationSelect';
import {
  FrontendUpdateNotification,
  HelpBanner,
  PageSidebarCalendar,
} from '@app/components/Page';
import { PREFIX } from '@app/constants/appPrefix';
import {
  usePage,
  usePageEffects,
  useBillingBlock,
  useSidebarOpenKeys,
} from '@app/providers/PageProvider/hooks';
import { PageContext } from '@app/providers/PageProvider/PageContext';
import type {
  Options,
  PageContextInterface,
} from '@app/providers/PageProvider/PageProvider.types';
import { useTechSupportModeHotkey } from '@app/providers/TechSupportMode/useTechSupportModeHotkey';
import { NotificationsBell } from '@app/shared/components/NotificationsBell';
import { SocialIntegrationsWarning } from '@app/shared/components/SocialIntegrationsWarning';

import '@app/components/Page/styles/Page.desktop.scss';
import '@app/components/Page/styles/Page.mobile.scss';

const { Sider } = Layout;

// Ширина развёрнутого сайдбара считается от блоков уведомлений
// (.SidebarBanner, max-width 216px) плюс поля по 8px: раньше было 250px, и
// разница уходила в пустые полосы по бокам. Свёрнутая ширина не меняется.
const SIDEBAR_CONTENT_WIDTH = 216;
const SIDEBAR_SIDE_PADDING = 8;
const SIDER_WIDTH = `${SIDEBAR_CONTENT_WIDTH + SIDEBAR_SIDE_PADDING * 2}px`;
const SIDER_COLLAPSED_WIDTH = '96px';
// Аватар в шапке бокового меню: вдвое меньше прежних 60px, без обводки
// (task/11120); размер совпадает с .image в Page.desktop.scss.
const SIDEBAR_AVATAR_SIZE = 30;

export const PageProvider = () => {
  const [options, setOptions] = useState<Options>({
    disableBreadcrumbs: false,
    disableContentStyles: false,
  });

  const {
    pageKey,
    pageTitle,
    memoizedNavigation,
    colorBgContainer,
    photo,
    nameFirst,
    nameLast,
    isMobile,
    titleData,
    pageTitleDataTestId,
    isPageWithExtraTitle,
    isHelpBoxOpened,
    setIsHelpBoxOpened,
    renderCreateBugReportModal,
    openCreateBugReportModal,
    homePageRedirect,
    getCountriesHandler,
    storeRoutesHandler,
    connectListenUser,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    items,
    isNewAppVersion,
    reloadAppWithCurrentUrl,
    selectedDate,
    onCalendarSelect,
    openCalendarDialog,
    handleClickByMenuItem,
    menuStateButtonClickHandler,
    goBackHandler,
    breadcrumbsItems,
    isCalendarDialogOpen,
    handleCalendarDialogClose,
    authenticateHandler,
    isSessionCheckPostponed,
    fetchBranchLightHandler,
    isLogoutModalOpen,
    setIsLogoutModalOpen,
    isBranchSelectModalOpen,
    setBranchSelectModal,
    hideMenuSidebar,
    isSmall,
    isFull,
    openSelectBranchModal,
    branchSelectValue,
    showCalendar,
    showPage,
    menuSidebarState,
    contentStyle,
    isCalendarPage,
    showFullSidebar,
    isLoadingPage,
    setTitleData,
    isAuth,
    isNone,
    isSupportChatVisible,
  } = usePage(options);

  usePageEffects({
    homePageRedirect,
    getCountriesHandler,
    storeRoutesHandler,
    connectListenUser,
    authenticateHandler,
    isSessionCheckPostponed,
    fetchBranchLightHandler,
    showFullSidebar,
  });

  const { isBlockedNonAdmin, showBlockedStub } = useBillingBlock();

  // Имя в шапке бокового меню: по клику по нему открывается меню профиля,
  // сам аватар больше не кнопка. Показывается только имя (task/11120),
  // фамилия — запасной вариант, если имени нет; полное имя — в title.
  const profileName = nameFirst || nameLast || '';
  const profileFullName = [nameFirst, nameLast].filter(Boolean).join(' ');

  // Общий для десктопного и мобильного меню стор раскрытых подменю: без него
  // Ant Menu сворачивает все списки при каждой перезагрузке страницы.
  const { openKeys, onOpenChange } = useSidebarOpenKeys();

  const value: PageContextInterface = useMemo(() => {
    return {
      isLoadingPage,
      isLogoutModalOpen,
      setIsLogoutModalOpen,
      isBranchSelectModalOpen,
      setBranchSelectModal,
      hideMenuSidebar,
      isSmall,
      isFull,
      openSelectBranchModal,
      branchSelectValue,
      showCalendar,
      showPage,
      menuSidebarState,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      items,
      isNewAppVersion,
      selectedDate,
      onCalendarSelect,
      openCalendarDialog,
      handleClickByMenuItem,
      menuStateButtonClickHandler,
      goBackHandler,
      breadcrumbsItems,
      isCalendarDialogOpen,
      handleCalendarDialogClose,
      pageKey,
      pageTitle,
      memoizedNavigation,
      colorBgContainer,
      photo,
      nameFirst,
      nameLast,
      isMobile,
      titleData,
      pageTitleDataTestId,
      isPageWithExtraTitle,
      isHelpBoxOpened,
      setIsHelpBoxOpened,
      renderCreateBugReportModal,
      openCreateBugReportModal,
      contentStyle,
      isCalendarPage,
      setOptions,
      setTitleData,
    };
  }, [
    isLoadingPage,
    isLogoutModalOpen,
    setIsLogoutModalOpen,
    isBranchSelectModalOpen,
    setBranchSelectModal,
    hideMenuSidebar,
    isSmall,
    isFull,
    openSelectBranchModal,
    branchSelectValue,
    showCalendar,
    showPage,
    menuSidebarState,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    items,
    isNewAppVersion,
    selectedDate,
    onCalendarSelect,
    openCalendarDialog,
    handleClickByMenuItem,
    menuStateButtonClickHandler,
    goBackHandler,
    breadcrumbsItems,
    isCalendarDialogOpen,
    handleCalendarDialogClose,
    pageKey,
    pageTitle,
    memoizedNavigation,
    colorBgContainer,
    photo,
    nameFirst,
    nameLast,
    isMobile,
    titleData,
    pageTitleDataTestId,
    isPageWithExtraTitle,
    isHelpBoxOpened,
    setIsHelpBoxOpened,
    renderCreateBugReportModal,
    openCreateBugReportModal,
    contentStyle,
    isCalendarPage,
    setOptions,
    setTitleData,
  ]);

  useTechSupportModeHotkey();

  if (isLoadingPage) {
    return <AppLoader />;
  }

  if (!isAuth) {
    return (
      <PageContext.Provider value={value}>
        <Outlet />
      </PageContext.Provider>
    );
  }

  return (
    <PageContext.Provider value={value}>
      <div className={`${PREFIX}page`}>
        <Layout>
          {!isMobile && !hideMenuSidebar && (
            <Sider
              className={cn('sider small-scrollbar', {
                ['sider-collapsed']: isSmall,
              })}
              trigger={null}
              collapsible
              collapsed={isSmall}
              width={SIDER_WIDTH}
              collapsedWidth={SIDER_COLLAPSED_WIDTH}
            >
              <div className="sider-container">
                <div
                  className="actions-container"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="image-container">
                    <div className="profile-row">
                      <div data-testid="user-avatar">
                        <Avatar
                          size={SIDEBAR_AVATAR_SIZE}
                          className="image for-tests-avatar-icon"
                          src={photo}
                          nameFirst={nameFirst}
                          nameLast={nameLast}
                        />
                      </div>
                      {isFull && (
                        <>
                          <Dropdown menu={{ items }}>
                            <button
                              type="button"
                              className="profile-name"
                              title={profileFullName}
                            >
                              {profileName}
                            </button>
                          </Dropdown>
                          {/* Колокольчик уведомлений в верхнем ряду шапки: имя
                              и филиал заняли место прежнего селекта, а строка
                              филиала теперь во всю ширину. */}
                          <div className="notifications-bell-row-inline">
                            <NotificationsBell />
                          </div>
                        </>
                      )}
                    </div>
                    {isFull && (
                      <>
                        <OrganizationSelect />
                        <button
                          type="button"
                          data-testid="branch-select"
                          className="branch-button"
                          onClick={openSelectBranchModal}
                        >
                          {branchSelectValue.label}
                        </button>
                      </>
                    )}
                  </div>
                  {isNewAppVersion && (
                    <FrontendUpdateNotification
                      isSmall={isSmall}
                      onClick={reloadAppWithCurrentUrl}
                    />
                  )}
                  <SocialIntegrationsWarning isSmall={isSmall} />
                  <div className="calendar-container">
                    {showCalendar && (
                      <PageSidebarCalendar
                        value={selectedDate}
                        onSelect={onCalendarSelect}
                      />
                    )}
                  </div>

                  {isSmall && (
                    <Button
                      type="text"
                      className="calendar-button"
                      onClick={openCalendarDialog}
                    >
                      <CalendarOutlined />
                      Календарь
                    </Button>
                  )}

                  {!isBlockedNonAdmin && (
                    <Menu
                      theme="dark"
                      mode="inline"
                      selectedKeys={[pageKey]}
                      items={memoizedNavigation}
                      onClick={handleClickByMenuItem}
                      rootClassName="no-tooltip"
                      // В свёрнутом Sider подменю открываются ховер-попапами —
                      // контролируемые openKeys там ломали бы их открытие.
                      {...(!isSmall && { openKeys, onOpenChange })}
                    />
                  )}
                  {/* Помощь живёт постоянно, а не сигналит о состоянии, как
                      баннеры обновления и сбоев интеграций, — поэтому стоит
                      последней, под списком разделов. */}
                  <HelpBanner
                    isSmall={isSmall}
                    onClick={() => setIsHelpBoxOpened(true)}
                  />
                </div>
                <div
                  className="collapse-button"
                  data-testid="collapse-menu"
                  onClick={menuStateButtonClickHandler}
                >
                  {isSmall ? <RightCircleOutlined /> : <LeftCircleOutlined />}
                  {!isSmall && (
                    <div className="collapse-button_title">Свернуть меню</div>
                  )}
                </div>
              </div>
            </Sider>
          )}

          {isMobile && (
            <>
              <div
                className={cn('mobile-sidebar-backdrop', {
                  'mobile-sidebar-backdrop--visible': !isNone,
                })}
                onClick={menuStateButtonClickHandler}
              />
              <div
                className={cn('mobile-sidebar small-scrollbar', {
                  'mobile-sidebar--open': !isNone,
                })}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div className="sider-container">
                  <div className="actions-container">
                    <div className="image-container">
                      <div className="profile-row">
                        <div data-testid="user-avatar">
                          <Avatar
                            size={SIDEBAR_AVATAR_SIZE}
                            className="image for-tests-avatar-icon"
                            src={photo}
                            nameFirst={nameFirst}
                            nameLast={nameLast}
                          />
                        </div>
                        <Dropdown menu={{ items }}>
                          <button
                            type="button"
                            className="profile-name"
                            title={profileFullName}
                          >
                            {profileName}
                          </button>
                        </Dropdown>
                        <div className="notifications-bell-row-inline">
                          <NotificationsBell />
                        </div>
                      </div>
                      <OrganizationSelect />
                      <button
                        type="button"
                        data-testid="branch-select"
                        className="branch-button"
                        onClick={openSelectBranchModal}
                      >
                        {branchSelectValue.label}
                      </button>
                    </div>
                    {isNewAppVersion && (
                      <FrontendUpdateNotification
                        onClick={reloadAppWithCurrentUrl}
                      />
                    )}
                    <SocialIntegrationsWarning isSmall={false} />
                    <div className="calendar-container">
                      {showCalendar && (
                        <PageSidebarCalendar
                          value={selectedDate}
                          onSelect={onCalendarSelect}
                        />
                      )}
                    </div>
                    {!isBlockedNonAdmin && (
                      <Menu
                        theme="dark"
                        mode="inline"
                        selectedKeys={[pageKey]}
                        items={memoizedNavigation}
                        onClick={handleClickByMenuItem}
                        rootClassName="no-tooltip"
                        openKeys={openKeys}
                        onOpenChange={onOpenChange}
                      />
                    )}
                    <HelpBanner onClick={() => setIsHelpBoxOpened(true)} />
                  </div>
                </div>
              </div>
            </>
          )}

          {showBlockedStub ? <BillingBlockedStub /> : <Outlet />}
        </Layout>
        <Dialog
          title="Календарь"
          isModalOpen={isCalendarDialogOpen}
          handleOk={handleCalendarDialogClose}
          handleCancel={handleCalendarDialogClose}
          footer={[
            <Button key="cancel" onClick={handleCalendarDialogClose}>
              Отменить
            </Button>,
          ]}
        >
          <PageSidebarCalendar
            value={selectedDate}
            onSelect={onCalendarSelect}
          />
        </Dialog>
        <BranchSelect
          open={isBranchSelectModalOpen}
          setBranchSelect={setBranchSelectModal}
        />
        <LogoutModal
          isModalOpen={isLogoutModalOpen}
          setIsModalOpen={setIsLogoutModalOpen}
        />
        <HelpBoxModal
          isOpened={isHelpBoxOpened}
          onOk={openCreateBugReportModal}
          onCancel={() => setIsHelpBoxOpened(false)}
        />

        {renderCreateBugReportModal()}
        {isSupportChatVisible && <SupportChatWidget />}
      </div>
    </PageContext.Provider>
  );
};
